import { BASE, request } from "./client";

export interface PoolPlaybookRow {
  playbook_id: string;
  display_name: string;
  manual_value: number | null;
  allowance: number;
  active: number;
  queued: number;
}

export interface PoolStatus {
  cap: number;
  active: number;
  queued: number;
  workers: number;
  manual_sum: number;
  warn_over_cap: boolean;
  playbooks: PoolPlaybookRow[];
}

export interface TrendPoint {
  ts: number;
  asr_ms: number | null;
  pilot_ms: number | null;
  talker_ms: number | null;
  tts_ttfb_ms: number | null;
  e2e_ttfb_ms: number | null;
  tts_rtf: number | null;
  lag_ms: number | null;
}

export interface PoolMetricsTrend {
  range: { start: number; end: number } | null;
  window_seconds: number;
  points: TrendPoint[];
}

export function getPoolStatus(): Promise<PoolStatus> {
  return request("/pool/status");
}

export function resizePool(cap: number): Promise<PoolStatus> {
  return request("/pool/resize", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ cap }),
  });
}

export function setPlaybookManual(playbookId: string, value: number): Promise<PoolStatus> {
  return request(`/pool/playbook/${encodeURIComponent(playbookId)}/manual`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ value }),
  });
}

export function clearPlaybookManual(playbookId: string): Promise<PoolStatus> {
  return request(`/pool/playbook/${encodeURIComponent(playbookId)}/manual`, {
    method: "DELETE",
  });
}

export function clearPlaybookQueue(playbookId: string): Promise<PoolStatus & { removed: number }> {
  return request(`/pool/playbook/${encodeURIComponent(playbookId)}/queue`, {
    method: "DELETE",
  });
}

export function getPoolMetricsTrend(windowSeconds: number): Promise<PoolMetricsTrend> {
  return request(`/pool/metrics/trend?window_seconds=${windowSeconds}`);
}

export function poolMetricsStreamUrl(): string {
  return `${BASE}/pool/metrics/stream`;
}

export interface CallsTrendBucket {
  ts: number;            // unix seconds, minute-aligned
  answered: number;
  unanswered: number;
}

export interface CallsTrend {
  window_seconds: number;
  summary: {
    total: number;
    answered: number;
    unanswered: number;
    rate: number;        // answered / total, 0..1
  };
  buckets: CallsTrendBucket[];
}

export function getPoolCallsTrend(windowSeconds: number): Promise<CallsTrend> {
  return request(`/pool/calls/trend?window_seconds=${windowSeconds}`);
}

export interface SystemHealthPoint {
  ts: number;
  lag_ms: number;
  active_threads: number;
  queue_depth: number;
  postcall_active: number;
  postcall_queue: number;
  webhook_active: number;
  webhook_queue: number;
  stream_lag: number;
}

export interface SystemHealthTrend {
  window_seconds: number;
  points: SystemHealthPoint[];
}

export function getSystemHealth(windowSeconds: number): Promise<SystemHealthTrend> {
  return request(`/pool/system-health?window_seconds=${windowSeconds}`);
}

export interface WorkerLagPoint {
  ts: number;
  lag_ms: number;
  active_calls: number;
  threads: number;
  queue_depth: number;
  ready_queue?: number;
}

export interface WorkerLagHistory {
  worker_id: string;
  worker_name: string;
  points: WorkerLagPoint[];
}

export interface WorkerLagTrend {
  window_seconds: number;
  workers: WorkerLagHistory[];
}

export function getWorkerLag(windowSeconds: number): Promise<WorkerLagTrend> {
  return request(`/pool/worker-lag?window_seconds=${windowSeconds}`);
}
